// "Fasteners, connectors & published capacities" — catalog authority levels
// and exact-row provenance. The article stays unpublished while the product
// blockers named below remain open.
import type { HelpArticle } from "../../types";
import {
  MAPLE_PROJECT,
  PRODUCT_CAPACITY_DEMO,
  PROPRIETARY_HANGER_DEMO,
} from "../../../calculations/calcPreviewFixtures";

const pounds = (value: number): string => value.toLocaleString("en-US");

export const productLookupArticle: HelpArticle = {
  id: "product-lookup",
  title: "Fasteners, connectors & published capacities",
  description:
    "Distinguish engine fastener data, reference-only hardware matches, exact capacity rows, and installation-grain proprietary selections.",
  tags: ["catalog", "fastener", "connector", "capacity", "stable key", "provenance"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "The connection database contains records with different authority. A standard nail can seed an engine fastener model. A hardware match can identify a possible product without checking it. An exact published capacity row can price one direction and condition. Treating those three operations as interchangeable breaks the audit trail.",
    },

    { kind: "heading", level: 2, text: "Fastener data is model-specific", id: "fasteners" },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "Connection sheet", "Browse database"] },
    {
      kind: "paragraph",
      text: "The Structural fasteners tab separates generic **Standard NDS nails** from **Proprietary and evaluated fasteners**. A standard nail row carries its size, diameter, length, Fyb, and engine-data source; no brand is required. Selecting it on a Nail sheet fills the compatible nail inputs. Evaluated fastener rows carry product identity, geometry, Fyb, source, and published reference values, but the button is enabled only when the open sheet uses the compatible capacity model.",
    },
    {
      kind: "paragraph",
      text: "A catalog screw does not become a nail because both are dowel fasteners. On a Nail sheet it is labeled **Reference**. On the compatible Wood Screw sheet, selecting it can seed diameter and bending-yield strength, while installation-specific penetration remains an entered and reviewed condition. The picker prevents a product row from silently changing the calculation method behind the sheet.",
    },
    {
      kind: "shot",
      shotId: "product-fastener-catalog",
      caption: "Engine standard-nail data and evaluated product rows remain separate, with compatibility controlling the action.",
    },

    { kind: "heading", level: 2, text: "Reference-only hardware is not a check", id: "reference-only" },
    {
      kind: "paragraph",
      text: "The Proprietary hardware tab is a discovery surface. Its rows show product family, manufacturer, occurrence count, installation count, and published reference maxima. Those maxima can come from different species, schedules, directions, load-duration bases, and installations. The column therefore says **Published reference range**, the action says **Attach reference**, and the selected sheet says the attachment is not an engine pass/fail check.",
    },
    {
      kind: "paragraph",
      text: "Use a reference to preserve the product identity you are investigating. To produce a capacity verdict, switch the unified sheet to Hardware and select a calculation-ready capacity row, or use a standalone proprietary calculator whose engine can resolve the required installation condition. Do not copy a reference maximum into a demand/capacity comparison.",
    },
    {
      kind: "shot",
      shotId: "product-hardware-reference",
      caption: "Every visible cue keeps proprietary hardware matches on the reference side of the boundary.",
    },

    { kind: "heading", level: 2, text: "Exact published capacity rows", id: "exact-capacity" },
    {
      kind: "paragraph",
      text: `On a Hardware sheet, the picker receives the sheet's design method, direction, and demand. For ${PRODUCT_CAPACITY_DEMO.mark}, those browsing chips are ${PRODUCT_CAPACITY_DEMO.designMethod}, ${PRODUCT_CAPACITY_DEMO.direction}, and ${pounds(PRODUCT_CAPACITY_DEMO.demandLb)} lb. **Capacity ≥ ${pounds(PRODUCT_CAPACITY_DEMO.demandLb)} lb** is an optional filter; it narrows discovery but does not decide whether the installation, species, schedule, finish, or other conditions match the project.`,
    },
    {
      kind: "paragraph",
      text: "Each capacity row keeps the number with its direction, design method, load-duration basis, installation label, condition summary, schedule, and source. Selecting a row persists its stable identity and display sidecars. Calculation does not trust the modal payload: the server resolves the key again. Changing the design method clears the method-specific selection before the next browse. Changing the manual direction does not clear it: the engine refuses the mismatch, the sheet keeps the prior result visible under a Latest recalculation failed warning, and the user must select an exact row for the new direction.",
    },
    {
      kind: "shot",
      shotId: "product-capacity-filter",
      caption: "Sheet context and the capacity filter rank exact rows for review; neither proves applicability.",
    },
    { kind: "registry-field", calculatorId: "connector_product_capacity", fieldKey: "manual_design_demand_lb" },
    {
      kind: "values",
      caption: `${PRODUCT_CAPACITY_DEMO.mark}'s deterministic exact-row check.`,
      rows: [
        { label: "Capacity identity", value: `${PRODUCT_CAPACITY_DEMO.capacityKey} · set ${PRODUCT_CAPACITY_DEMO.capacitySetId}` },
        { label: "Direction and basis", value: `${PRODUCT_CAPACITY_DEMO.direction} · ${PRODUCT_CAPACITY_DEMO.designMethod} · CD ${PRODUCT_CAPACITY_DEMO.loadDurationCd.toFixed(1)}` },
        { label: "Demand / capacity", value: `${pounds(PRODUCT_CAPACITY_DEMO.demandLb)} / ${pounds(PRODUCT_CAPACITY_DEMO.capacityLb)} lb` },
        { label: "Result", value: `${(PRODUCT_CAPACITY_DEMO.dcr * 100).toFixed(1)}% · Pass` },
        { label: "Installation", value: `${PRODUCT_CAPACITY_DEMO.installationKey} · ${PRODUCT_CAPACITY_DEMO.installationCondition}` },
        { label: "Source", value: `${PRODUCT_CAPACITY_DEMO.sourceDocument} · ${PRODUCT_CAPACITY_DEMO.sourceTable} · p. ${PRODUCT_CAPACITY_DEMO.sourcePage}` },
      ],
    },
    {
      kind: "paragraph",
      text: `The ${PRODUCT_CAPACITY_DEMO.mark} result repeats ${pounds(PRODUCT_CAPACITY_DEMO.demandLb)} lb demand, ${pounds(PRODUCT_CAPACITY_DEMO.capacityLb)} lb capacity, and ${(PRODUCT_CAPACITY_DEMO.dcr * 100).toFixed(1)}% utilization in both the summary and detailed check. The trace retains the capacity key, while Calculation basis identifies the published-evaluation standard and resolved source. Review those identifiers with the number; a passing percentage alone cannot prove the selected row describes the work.`,
    },
    {
      kind: "shot",
      shotId: "product-capacity-result",
      caption: `${PRODUCT_CAPACITY_DEMO.mark}: exact demand/capacity result, selected-row trace, and calculation basis.` ,
    },

    { kind: "heading", level: 2, text: "Installation-grain proprietary selection", id: "exact-installations" },
    {
      kind: "paragraph",
      text: `The generic proprietary picker stores an installation-grain key. The known complete example shown here is ${PROPRIETARY_HANGER_DEMO.primary}: \`${PROPRIETARY_HANGER_DEMO.stableKey}\`, ${PROPRIETARY_HANGER_DEMO.installation}, ${PROPRIETARY_HANGER_DEMO.condition}. Its scalar verification fixture carries ${pounds(PROPRIETARY_HANGER_DEMO.downloadCapacityLb)} lb download and ${pounds(PROPRIETARY_HANGER_DEMO.upliftCapacityLb)} lb uplift from ${PROPRIETARY_HANGER_DEMO.sourceDocument}, ${PROPRIETARY_HANGER_DEMO.sourceTable}, page ${PROPRIETARY_HANGER_DEMO.sourcePage}.`,
    },
    {
      kind: "paragraph",
      text: "Expanding a row exposes the stable key, facts, and source URL. That is enough to audit what was selected, but installation-grain selection is not proof that a condition-specific engine row exists. Browse ranking is discovery only. The server's exact re-resolution and the engine's acceptance or refusal are authoritative at calculation time.",
    },
    {
      kind: "shot",
      shotId: "product-exact-installation",
      caption: "A complete LUS24 scalar example expanded to show its persisted key, visible condition facts, and source.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Publication blockers in the current manual UI",
      body: "Dowel load fields are labeled factored although the engine combines their load-type components. Hanger rows that require load_duration_cd cannot complete because the manual schema exposes no such field. The generic picker calls an installation-grain choice an exact check row even though the engine may require a condition-specific row. Hold-down ranking can use child wood/load/anchor channels that the current engine resolver does not consume, so a ranked row can still be rejected. These four contracts require founder/product and PE resolution before publication.",
    },
    {
      kind: "callout",
      variant: "tip",
      title: "What ranking means",
      body: "Search, demand chips, and capacity filters decide which records appear first. They do not approve substitutions, interpolate missing conditions, or establish that every downstream load-path channel is complete.",
    },

    { kind: "related", topics: ["connection-checks", "fastening-escalation", "linking-reactions"] },
  ],
};
